import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import moment, { MomentInput } from 'moment';
import { Subscription } from 'rxjs';
import { ICvSubscriptionScheduleData, ICvSubscriptionScheduleDataDates, ReportSubscription, ReportSubscriptionSchedule } from 'src/shared/models/subscriptions.model';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { SubscriptionSchedulerService } from './subscription-scheduler.service';
import { TimezoneService } from '../services/timezone.service';
import { ToastrManager } from '@niceltd/sol/toastr';
import { CVLocalizationService } from '../services/cv-localization.service';

type SelectValues = {
    id?: number;
    selected: boolean;
    name: string;
    localizedName?: string;
    shortName?: string;
    value?: number;
};

@Component({
    selector: 'cv-subscription-scheduler',
    templateUrl: './subscription-scheduler.component.html',
    styleUrls: ['./subscription-scheduler.component.scss']
})
export class SubscriptionSchedulerComponent implements OnInit, AfterViewInit, OnChanges {
    /**
     * VERY IMPORTANT
     * Defaults to false if undefined. Otherwise, if true:
     * Uses a trimmed, embeddable template (no popup, no header, no footer) rather than a standalone modal. 
     * Does not call any factory functions. Schedule will only be returned through the "retrieveSchedule" callback for the parent directive to manage. (Useful for multi-step processes.)
     * The titlebar won't be displayed, so the given subscriptionTitle will not be displayed.
     */
    @Input() embedMode = false;

    /** What type of report is being generated. Must be set. */
    @Input() reportType: number;

    /** If the ReportSubscriptionID is known, it should be passed in. If this is unknown (such as for a new subscription), do not define this or set it to "-1" to indicate a new subscription. */
    @Input() subscriptionId = -1;

    /** Got yourself a schedule? Pass it in! Check the $thisSubSched.applyExistingSchedule() function for properties. */
    @Input() existingSchedule: ReportSubscription = null;

    /** Triggers the save schedule and will callback to the saved or saveFailed @Output */
    @Input() triggerSave: EventEmitter<void>;

    /** itle text in the modal. Will default to "Report Subscription" if not set. */
    @Input() subscriptionTitle = "Report Subscription";

    /** ID for the item you're creating a subscription for. */
    @Input() relatedId: number = null;

    /** Key for the item you're creating a subscription for. */
    @Input() relatedKey: string = null;

    /** Tells the directive to show markers for required or invalid fields. */
    @Input() showInvalid: boolean = false;
    /** Timezone for embedded modal, captured from widgets*/
    @Input() timezone: string = null;
    /** If this is is opened on the dashboard, the toast message will be different */
    @Input() isDashboardModal: boolean = true;

    @Output() saved = new EventEmitter<ICvSubscriptionScheduleData>();
    @Output() saveFailed = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    private _triggerSaveSubscription: Subscription;
    private static _emailRegex = /^[A-Z0-9._%+\'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i; 
    ownerEmail: string;
    initialized = false;
    isSaving = false;
    isLoading = false;
    dateFormat = CVAngularDateFormat;
    timeFormat = CVAngularTimeFormat;
    hoveredRecipient = -1;
    showDeleteButton = false;
    hasTimeZone = false;

    // Status for form validation:
    isRecipientTextareaInvalid = false;
    isSendEveryInvalid = false;
    schedule: ICvSubscriptionScheduleDataDates = null;
    newRecipients = "";

    //#region UI Variables
    //Based on ClearView.Admin.ReportSubscriptions.Enums.ExportType.
    exportTypes: SelectValues[] = [];

    //Based on ClearView.Admin.ReportSubscriptions.Enums.ScheduleType.
    frequencies: SelectValues[] = [];

    //Based on ClearView.Quality.Metrics.Enums.DOW.
    daysOfWeek: SelectValues[] = [];

    //Based on ClearView.Admin.ReportSubscriptions.Enums.RecurringDisplayType
    recurringDisplayValues: SelectValues[] = [];

    weeksOfMonth: SelectValues[] = [];

    weeksOfMonthFull: string[] = [];
    weeksOfMonthSelected: string[] = [];

    daysOfMonth: SelectValues[] = [];
    daysOfMonthFull: string[] = []; // 3 copies of the same data, because the md-select directive doesn't support arrays-of-objects easily and quick, ugly solutions are preferred over clean solutions for arbitrary deadlines.
    daysOfMonthSelected: string[] = [];

    selectDaysPlaceholder: string = "Select Day(s) of Month";
    selectWeeksPlaceholder: string = "Select Week(s) of Month";
    commaSeparatePlaceholder: string = "Please comma separate multiple emails.";
    //#endregion


    constructor(
        private schedulerService: SubscriptionSchedulerService,
        private dashboardService: DashboardService,
        private localizationService: CVLocalizationService,
        private toastrManager: ToastrManager,
        public timeZoneService: TimezoneService,
    ) {
        for (let i = 1; i <= 31; i++) {
            const displayName = (i != 31) ? i.toString() : 'Last';

            //Push to the backing array
            this.daysOfMonth.push({
                id: i,
                value: i,
                selected: false,
                name: displayName,
            });

            this.daysOfMonthFull.push(displayName);
        }
    }

    async ngOnInit() {
        const promises: Promise<any>[] = [
            this.localizationService.GetSection("Subscriptions_Dashboard"),
            this.localizationService.GetSection("Common"),
            this.getOwnerEmail()
        ];
        await Promise.all(promises);

        this.initialize();

        if (!this.embedMode)
            this.setTimeZoneName();

        this.hasTimeZone = !(this.timezone === null || this.timezone === "Not Set" || this.timezone === "");
    }

    ngAfterViewInit() {

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.triggerSave) {
            if (this._triggerSaveSubscription)
                this._triggerSaveSubscription.unsubscribe();
            this._triggerSaveSubscription = this.triggerSave.subscribe(() => this.save());
        }
    }

    initialize() {
        this.createSelectedValues();
        this.resetScheduleModel();

        if (!this.subscriptionTitle)
            this.subscriptionTitle = "Subscription";

        if (this.subscriptionId == -1)
            this.subscriptionId = null;

        this.schedule.subscriptionId = this.subscriptionId;

        if (this.existingSchedule != null) {
            this.applyExistingSchedule();
        } else {
            this.getSubscription();
        }
        this.initialized = true;
    }

    createSelectedValues() {
        this.exportTypes = [
            {
                value: 1,
                name: "Excel",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Excel", "Excel"),
                selected: false,
            },
            {
                value: 0,
                name: "Excel Without Objectives",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Excel_Without_Objectives",
                    "Excel Without Objectives"),
                selected: false,
            },
        ];

        // Only add the CSV option if the ReportType is Module
        if (this.reportType === 1) {
            this.exportTypes.push({
                value: 2,
                name: 'CSV',
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "CSV", "CSV"),
                selected: false,
            });
        }

        this.frequencies = [
            {
                value: 1,
                name: "Daily/Weekly",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Daily", "Daily/Weekly"),
                selected: false,
            },
            {
                value: 2,
                name: "Day(s) of Month",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Days_of_Month", "Day(s) of Month"),
                selected: false,
            },
            {
                value: 3,
                name: "Week(s) of Month",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Weeks_of_Month", "Week(s) of Month"),
                selected: false,
            },
        ];

        const sunday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Sunday", "Sunday");
        const monday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Monday", "Monday");
        const tuesday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Tuesday", "Tuesday");
        const wednesday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Wednesday", "Wednesday");
        const thursday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Thursday", "Thursday");
        const friday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Friday", "Friday");
        const saturday = this.localizationService.getCachedText("Subscriptions_Dashboard", "Saturday", "Saturday");

        this.daysOfWeek = [
            {
                value: 1,
                name: "Sunday",
                localizedName: sunday,
                shortName: sunday.charAt(0),
                selected: true,
            },
            {
                value: 2,
                name: "Monday",
                localizedName: monday,
                shortName: monday.charAt(0),
                selected: true,
            },
            {
                value: 4,
                name: "Tuesday",
                localizedName: tuesday,
                shortName: tuesday.charAt(0),
                selected: true,
            },
            {
                value: 8,
                name: "Wednesday",
                localizedName: wednesday,
                shortName: wednesday.charAt(0),
                selected: true,
            },
            {
                value: 16,
                name: "Thursday",
                localizedName: thursday,
                shortName: thursday.charAt(0),
                selected: true,
            },
            {
                value: 32,
                name: "Friday",
                localizedName: friday,
                shortName: friday.charAt(0),
                selected: true,
            },
            {
                value: 64,
                name: "Saturday",
                localizedName: saturday,
                shortName: saturday.charAt(0),
                selected: true,
            },
        ];

        this.recurringDisplayValues = [
            //Names are intentionally short and lower cased.
            {
                value: 0,
                name: "mins",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Mins", "mins"),
                selected: false
            },
            {
                value: 1,
                name: "hours",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Hours", "hours"),
                selected: false
            },
            {
                value: 2,
                name: "days",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Days", "days"),
                selected: false
            },
        ];

        this.weeksOfMonth = [
            {
                selected: false,
                name: "First",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "First", "First"),
            },
            {
                selected: false,
                name: "Second",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Second", "Second"),
            },
            {
                selected: false,
                name: "Third",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Third", "Third"),
            },
            {
                selected: false,
                name: "Fourth",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Fourth", "Fourth"),
            },
            {
                selected: false,
                name: "Last",
                localizedName: this.localizationService.getCachedText("Subscriptions_Dashboard", "Last", "Last"),
            },
        ];

        this.weeksOfMonthFull = this.weeksOfMonth.map(week => week.localizedName);
        this.selectDaysPlaceholder = this.localizationService.getCachedText("Subscriptions_Dashboard", "Select_Days_Placeholder",
            "Select Day(s) of Month");
        this.selectWeeksPlaceholder = this.localizationService.getCachedText("Subscriptions_Dashboard", "Select_Weeks_Placeholder",
            "Select Week(s) of Month");
        this.commaSeparatePlaceholder = this.localizationService.getCachedText("Subscriptions_Dashboard", "Instruction_Recipient",
            "Please comma separate multiple emails.");
    }

    resetScheduleModel() {
        this.schedule = {
            scheduleID: null, //May not exist if it is a new subscription.
            reportType: -1,
            relatedKey: null,
            relatedID: null,
            startDate: new Date(),
            endDate: null,
            exportType: 1,
            frequency: 1,
            daysOfWeek: [],
            daysOfMonth: [],
            weeksOfMonth: [],
            firstExportTime: new Date(),
            extraExportTimes: [],
            recipients: [],
            sendToMe: true,
            repeat: false,
            repeatOptions: {
                isRepeat: false,
                repeatValue: 1, // Default to one hour, but the minimum is 15 minutes
                repeatType: 1, // hours
                repeatEnd: moment().endOf("day").toDate(), // 11:59:59pm today
            },
            displayRecipients: false,
        };
    }

    /**
     * This function is used to test if an email address appears to be valid.  It's not very exhaustive, but it works for the vast majority of cases.
     * @param {string} email Trimmed email string to test for validity.
     */
    isValidEmail(email: string) {
        if (typeof email !== "string" || email.length < 1)
            return false;
        return SubscriptionSchedulerComponent._emailRegex.test(email);
    }

    onSelectedDaysChange() {
        this.schedule.daysOfWeek = this.daysOfWeek.map(x => x.selected);
    }

    onChangeSelectedDOM() {
        this.schedule.daysOfMonth = this.daysOfMonthFull.map(dom => this.daysOfMonthSelected.includes(dom));
    }

    getSelectedDOMText() {
        //Selected items are tracked as string entries in the list-bound array. 
        //  If it's in there, it's selected. If it's not in there, it's not selected. So we only need to count how many items are int he array of selected items.
        return this.daysOfMonthSelected.length > 0 ? this.daysOfMonthSelected.length + ' selected' : 'Select Day(s) of Month';
    }

    onChangeSelectedWOM() {
        this.schedule.weeksOfMonth = this.weeksOfMonthFull.map(dom => this.weeksOfMonthSelected.includes(dom));
    }

    getSelectedWOMText() {
        //Selected items are tracked as string entries in the list-bound array. 
        //  If it's in there, it's selected. If it's not in there, it's not selected. So we only need to count how many items are int he array of selected items.
        return this.weeksOfMonthSelected.filter(week => week).length > 0 ? this.weeksOfMonthSelected.length + ' selected' : 'Select Week(s) of Month';
    }

    removeTime(index: number) {
        this.schedule.extraExportTimes.splice(index, 1);
    }

    /**
    * This function is called when the add/plus button is clicked.  Only valid emails are added to the recipients list, the rest of the invalid values are kept in the box.
    */
    addRecipients() {
        //Check if the input is only whitespace or empty.
        if (this.newRecipients.trim() === '')
            return;

        let badValues = "";

        //Split on commas, then push a trimmed version (remove spaces and newlines) to the recipients list.
        this.newRecipients.split(',').forEach(r => {
            let rTrim = r.trim();
            if (rTrim.length == 0)
                return;

            if (this.isValidEmail(rTrim)){
                const isDuplicate = this.schedule.recipients.find(recipient => recipient.toLowerCase() == rTrim.toLowerCase());
                if(rTrim.toLowerCase() == this.ownerEmail.toLowerCase()){
                    if(!this.schedule.sendToMe && !isDuplicate)
                        this.schedule.recipients.push(rTrim);
                }
                else{
                    if(!isDuplicate)
                        this.schedule.recipients.push(rTrim);
                }
            }
            else {
                if (badValues.length > 0) {
                    badValues += ","
                }
                badValues += r;
            }
        });

        //Empty the new recipients receptacle.
        this.newRecipients = badValues;
        this.validateRecipients();
    }

    removeRecipient(index: number) {
        this.schedule.recipients.splice(index, 1);
    }

    onHoverRecipient(active: boolean, index: number) {
        if (active) {
            this.hoveredRecipient = index;
        } else if (this.hoveredRecipient == index) {
            this.hoveredRecipient = -1;
        }
    }

    removeOwner(){
        if(!this.schedule.sendToMe) return;

        const index = this.schedule.recipients.indexOf(this.ownerEmail);
        if(index > -1)
            this.schedule.recipients.splice(index, 1);
    }

    /**
     * This function is used to mark the textarea as invalid when emails don't properly validate
     */
    validateRecipients() {
        if (this.newRecipients.trim().length == 0) {
            this.isRecipientTextareaInvalid = false;
            return;
        }

        var emails = this.newRecipients
            .split(",")
            .map(s => s.trim()) // trim each split string
            .filter(e => e.length > 0); // exclude empty strings
        this.isRecipientTextareaInvalid = emails.some(e => !this.isValidEmail(e));
    }

    /**
     * This function is used to mark the Send Every field as invalid, when its value is less than 15 minutes
     */
    validateSendEvery() {
        var value = this.schedule.repeatOptions.repeatValue;
        if (typeof value !== "number") {
            this.isSendEveryInvalid = true;
            return;
        }

        var multiplier = 1;
        switch (this.schedule.repeatOptions.repeatType) {
            case 0:
                multiplier = 1;
                break;
            case 1:
                multiplier = 60;
                break;
            case 2:
                multiplier = 60 * 24;
                break;
            default:
                multiplier = 0;
        }

        value *= multiplier;
        if (value >= 15) {
            this.isSendEveryInvalid = false;
        } else {
            this.isSendEveryInvalid = true;
        }
    }

    /**
     * This function checks that there is at least one recipient for the subscription.
     */
    checkRecipientAdded() {
        if (this.schedule.sendToMe) {
            return true;
        } else if (this.schedule.recipients.length == 0) {
            return false;
        }
        return true;
    };

    async getOwnerEmail() {
        try {
            this.ownerEmail = await this.schedulerService.getSubscriptionOwner();
        } catch (error) {
            console.log(error);
        }
    }

    setTimeZoneName() {
        const dash = this.dashboardService.availableDashboards.find(dash => dash.DashboardKey === this.schedule.relatedKey);

        let tzName = "";
        const notSet = "Not Set";

        // Time zone for the dashboard modal
        if (dash?.TimeZoneInfoName)
            tzName = dash.TimeZoneInfoName;
        // Time zone is set for the user
        else if ((this.timeZoneService.dataObj?.selectedTimeZone ?? notSet) !== notSet)
            tzName = this.timeZoneService.dataObj.selectedTimeZone;
        // Time zone is set for the company
        else if ((this.timeZoneService.dataObj?.defaultCompanyTimeZone ?? notSet) !== notSet)
            tzName = this.timeZoneService.dataObj.defaultCompanyTimeZone;

        this.timezone = tzName;
    }

    async getSubscription() {
        if(this.schedule.relatedID == null)
            this.schedule.relatedID = this.relatedId;

        if (this.schedule.relatedKey == null)
            this.schedule.relatedKey = this.relatedKey;

        //Try to retrieve a subscription. If one doesn't exist, an empty object with an ID of -1 will be returned.
        if (this.relatedId && this.relatedKey) {
            this.isLoading = true;

            // This factory function gets a combination object containing both the ReportSubscription and ReportSubscriptionSchedule
            const response = await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(this.relatedKey, this.relatedId);

            if (response.ReportSubscription.ReportSubscriptionID == -1) {
                this.isLoading = false;
                return;
            }

            this.showDeleteButton = true;

            this.schedule.sendToMe = response.ReportSubscription.SendToOwner;
            this.schedule.title = response.ReportSubscription.ReportSubscriptionName;
            this.schedule.folder = response.ReportSubscription.Folder;
            this.schedule.subscriptionId = response.ReportSubscription.ReportSubscriptionID;
            this.schedule.reportType = response.ReportSubscription.ReportType;
            this.schedule.endDate = !!this.existingSchedule ? moment(this.existingSchedule.EndDate).toDate() : 
                response.ReportSubscription.EndDate ? moment(response.ReportSubscription.EndDate).toDate() : null;
            this.schedule.exportType = response.ReportSubscription.ExportType;
            if (response.ReportSubscriptionSchedule != null) {
                this.schedule.startDate = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.schedule.frequency = response.ReportSubscriptionSchedule.ScheduleType;
                this.schedule.firstExportTime = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.schedule.extraExportTimes = (response.ReportSubscriptionSchedule.ExtraExportTimes != undefined) ? response.ReportSubscriptionSchedule.ExtraExportTimes.map(d => moment(d).toDate()) : [];
            } else {
                this.schedule.startDate = new Date();
                this.schedule.frequency = 1;
                this.schedule.firstExportTime = new Date();
                this.schedule.extraExportTimes = [];
            }
            this.schedule.daysOfWeek = [];
            this.schedule.daysOfMonth = [];
            this.schedule.weeksOfMonth = [];
            this.schedule.recipients = response.ReportSubscription.RecipientEmails;
            this.schedule.displayRecipients = response.ReportSubscription.DisplayRecipients;

            //If sendToMe is checked, remove the display value. 
            //  This is for cosmetic purposes. That way we don't have to update the checkbox state or recipients list state based on the others' state (is checked, contains owner's email, etc).
            //  As long as the checkbox is checked, the controller will keep the owner's email in the recipients.
            // Making sure that during ngOnInit(), this.ownerEmail comes with the ownerEmail value.
            if (this.ownerEmail == undefined) {
                await this.getOwnerEmail();
            }

            if (this.schedule.sendToMe && this.ownerEmail != undefined) {
                let cleanedOwnerEmailChars = this.ownerEmail.split('');
                ['(', ')'].forEach(c => {
                    if (cleanedOwnerEmailChars.indexOf(c) > -1)
                        cleanedOwnerEmailChars.splice(cleanedOwnerEmailChars.indexOf(c), 1);
                });

                const cleanedOwnerEmail = cleanedOwnerEmailChars.join('');
                const recipientOwnerPositionIndex = this.schedule.recipients.indexOf(cleanedOwnerEmail);
                if (recipientOwnerPositionIndex > -1)
                    this.schedule.recipients.splice(recipientOwnerPositionIndex, 1);
            }

            //Repeat stuff
            //  If repeat is checked, it uses the given values. Otherwise, it uses blank values.
            if (response.ReportSubscriptionSchedule
                && response.ReportSubscriptionSchedule.RecurringMinutes > 0
                && response.ReportSubscriptionSchedule.RecurringDisplayType != null
                && response.ReportSubscriptionSchedule.EndDateTime != null) {
                this.schedule.repeatOptions.isRepeat = true;
                this.schedule.repeatOptions.repeatType = response.ReportSubscriptionSchedule.RecurringDisplayType;
                // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
                if (this.schedule.repeatOptions.repeatType == 1) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 60) | 0;
                } else if (this.schedule.repeatOptions.repeatType == 2) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 1440) | 0;
                } else {
                    this.schedule.repeatOptions.repeatValue = response.ReportSubscriptionSchedule.RecurringMinutes;
                }
                // This is done to avoid UTC to Local (in the endpoint) getting confused with daylight saving
                const endDateTime = moment(response.ReportSubscriptionSchedule.EndDateTime).toDate();
                var repeatEnd = moment().seconds(0)
                    .hours(endDateTime.getHours())
                    .minutes(endDateTime.getMinutes());
                this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
            } else {
                this.schedule.repeatOptions.isRepeat = false;
                this.schedule.repeatOptions.repeatValue = 1;
                this.schedule.repeatOptions.repeatType = 1; // hours
                this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
            }

            //Map the multi-select values to the corresponding display model objects.
            //Convert from one int to a list of enum-ints (see enums in framework for highest int value)
            //daysOfWeek
            this.schedule.daysOfWeek = [];
            this.daysOfMonthSelected = [];
            this.weeksOfMonthSelected = [];
            if (response.ReportSubscriptionSchedule) {
                let dowLimit = Math.pow(2, 7);
                for (var i = 1, n = 0; i < dowLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfWeek & i) == i) {
                        this.schedule.daysOfWeek[n] = true;
                        this.daysOfWeek[n].selected = true;
                    } else {
                        this.schedule.daysOfWeek[n] = false;
                        this.daysOfWeek[n].selected = false;
                    }
                }

                //daysOfMonth
                let domLimit = Math.pow(2, 31);
                for (var i = 1, n = 0; i < domLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfMonth & i) == i) {
                        this.schedule.daysOfMonth[n] = true;
                        this.daysOfMonthSelected.push(this.daysOfMonthFull[n]);
                        this.daysOfMonth[n].selected = true;
                    } else {
                        this.schedule.daysOfMonth[n] = false;
                        this.daysOfMonth[n].selected = false;
                    }
                }

                //weeksOfMonth
                let womLimit = Math.pow(2, 5);
                for (var i = 1, n = 0; i < womLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.WeeksOfMonth & i) == i) {
                        this.schedule.weeksOfMonth[n] = true;
                        this.weeksOfMonthSelected.push(this.weeksOfMonthFull[n]);
                        this.weeksOfMonth[n].selected = true;
                    } else {
                        this.schedule.weeksOfMonth[n] = false;
                        this.weeksOfMonth[n].selected = false;
                    }
                }
            }

            this.isLoading = false;
        }
    }

    async save() {
        if (this.isSaving)
            return;

        //Extract values from arrays of objects to arrays of primitive values
        //  Models from MD-Select directives are arrays of selected values ("1", "2"...), not selected indexes or flags.
        //  For each item in the full set, return if the "Selected" model contains its value (item.value).
        this.schedule.daysOfWeek = ([1, 3].indexOf(this.schedule.frequency) > -1) ? this.daysOfWeek.map(dow => dow.selected) : this.daysOfWeek.map(_ => false);
        this.schedule.daysOfMonth = this.daysOfMonthFull.map(dom => this.daysOfMonthSelected.includes(dom));
        this.schedule.weeksOfMonth = this.weeksOfMonthFull.map(wom => this.weeksOfMonthSelected.includes(wom));

        if (this.schedule.recipients.length > 100){
            this.schedule.displayRecipients = false;
        }

        if (!this.embedMode){
            this.schedule.reportType = this.reportType;
            if( this.schedule.description == null) 
                this.schedule.description = '';

            //When not in embedMode, it's assumed that the title needs to be manually set. If this.title is defined, use it. Otherwise, use a default value based on report type.
            if(this.subscriptionTitle != null) {
                this.schedule.title = this.subscriptionTitle;
            } else {
                switch (this.reportType) { //Based on ReportType enum in framework.
                    case 0: //Dashboard
                        this.schedule.title = "Dashboard Subscription";
                        break;
                    case 1: //Module
                        this.schedule.title = "Module Subscription";
                    default:
                        break;
                }
            }

            if (this.schedule.subscriptionId == null) {
                try
                {
                    const response = await this.schedulerService.createSubscriptionSchedule(this.schedule);

                    if (this.reportType == 0) {
                        // update dashboard slideout (for the icon)
                        this.dashboardService.availableDashboards.find(dash => {
                            if (dash.DashboardKey === this.schedule.relatedKey) {
                                dash.IsSubscribed = true;
                            }
                        });

                        this.dashboardService.availableDashboardsChanged.emit();
                        
                    }

                    this.closeModal();
                    const toastString = this.isDashboardModal ? "Subscription_Subscribed" : "Subscription_Created";
                    this.toastrManager.showToaster('success',
                        this.localizationService.getCachedText("Subscriptions_Dashboard", toastString));
                } catch(err) {
                    this.isSaving = false;
                    console.error('[CreateSubscriptionSchedule] Failed to save new subscription.', err);
                    err = err.data || err;
                    var errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            } else {
                try
                {
                    const response = await this.schedulerService.updateSubscriptionSchedule(this.schedule);
                    this.closeModal();
                    this.toastrManager.showToaster('success', this.localizationService.getCachedText("Subscriptions_Dashboard",
                        "Subscription_Updated"));
                } catch (err) {
                    console.error("Error updating subscription schedule", err);
                    err = err.data || err;
                    var errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            }
        }

        this.saved.emit(this.schedule);
    }

    clickCancel() {
        this.resetScheduleModel();
        this.closeModal();
    }

    clickDeleteSubscription() {
        // FIXME: Localize this
        var title = "Delete Confirmation";
        var message = "Are you sure you want to delete this subscription?<br/>";
        message += htmlEncode(this.schedule.title || this.subscriptionTitle || "");
        window['showConfirmDialog'](message, [
            {
                text: "Cancel",
                click: function () {
                    jQuery(".errorDialog")['dialog']("close");
                }
            },
            {
                text: "Delete",
                click: () => {
                    this.doDeleteSubscription();
                    jQuery(".errorDialog")['dialog']("close");
                }
            }
        ], title);
    }

    async doDeleteSubscription() {
        var dashboardKey = this.schedule.relatedKey;
        try
        {
            await this.schedulerService.deleteSubscriptionSchedule(this.schedule.subscriptionId);
            let dashboard = this.dashboardService.availableDashboards.find(dash => dash.DashboardKey === dashboardKey);
            if (dashboard)
                dashboard.IsSubscribed = false;
        } finally {
            this.clickCancel();
            this.saved.emit(null);
            this.toastrManager.showToaster('success', this.localizationService.getCachedText("Subscriptions_Dashboard", "Subscription_Deleted"));
        }
    }

    //Do not bind this directly
    closeModal() {
        //Mark the modal as closed
        this.closed.emit();
    }

    //#endregion

    //#region Existing Schedule Handlers
    async applyExistingSchedule() {
        const response = await this.schedulerService.getSubscriptionSchedule(this.existingSchedule.ReportSubscriptionID, this.existingSchedule.RelatedID, this.existingSchedule.RelatedKey);

        this.schedule.sendToMe = this.existingSchedule.SendToOwner;
        this.schedule.subscriptionId = this.existingSchedule.ReportSubscriptionID;
        this.schedule.startDate = moment(response.StartDateTime).toDate();
        this.schedule.endDate = (response.EndDate != null) ? moment(response.EndDate).toDate() : moment(this.existingSchedule.EndDate).toDate();
        this.schedule.exportType = (this.existingSchedule.ExportType == null) ? this.schedule.exportType : this.existingSchedule.ExportType;
        this.schedule.frequency = response.ScheduleType;
        this.schedule.daysOfWeek = [];
        this.schedule.daysOfMonth = [];
        this.schedule.weeksOfMonth = [];
        this.schedule.firstExportTime = moment(response.StartDateTime).toDate();
        this.schedule.extraExportTimes = (response.ExtraExportTimes != null) ? response.ExtraExportTimes.map(d => moment(d).toDate()) : [];
        this.schedule.recipients = (this.existingSchedule.RecipientEmails != null) ? [...this.existingSchedule.RecipientEmails] : this.existingSchedule.Recipients?.map(r => r.EmailAddress);
        this.schedule.displayRecipients = this.existingSchedule.DisplayRecipients ? this.existingSchedule.DisplayRecipients : false;
        //Getting the owner of the email in order to continue with the validation if sendToMe is checked.
        await this.getOwnerEmail();
        //If sendToMe is checked, remove the display value. 
        //  This is for cosmetic purposes. That way we don't have to update the checkbox state or recipients list state based on the others' state (is checked, contains owner's email, etc).
        //  As long as the checkbox is checked, the controller will keep the owner's email in the recipients.
        if(this.schedule.sendToMe) { 
            var recipientOwnerPositionIndex = this.schedule.recipients.indexOf(this.ownerEmail.replace('(', '').replace(')','')); //"(e@mail.com)" => "e@mail.com"
            if(recipientOwnerPositionIndex > -1)
                this.schedule.recipients.splice(recipientOwnerPositionIndex, 1);
        }

        // Repeat Options
        if (response.RecurringMinutes > 0) {
            this.schedule.repeatOptions.isRepeat = true;
            this.schedule.repeatOptions.repeatType = response.RecurringDisplayType;
            // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
            if (this.schedule.repeatOptions.repeatType == 1) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 60) | 0;
            } else if (this.schedule.repeatOptions.repeatType == 2) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 1440) | 0;
            } else {
                this.schedule.repeatOptions.repeatValue = response.RecurringMinutes;
            }
            const endDateTime = moment(response.EndDateTime).toDate();
            var repeatEnd = moment().seconds(0)
                .hours(endDateTime.getHours())
                .minutes(endDateTime.getMinutes());
            this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
        } else {
            this.schedule.repeatOptions.isRepeat = false;
            this.schedule.repeatOptions.repeatValue = 1;
            this.schedule.repeatOptions.repeatType = 1; // hours
            this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
        }

        //Map the multi-select values to the corresponding display model objects.
        //Convert from one int to a list of enum-ints (see enums in framework for highest int value)
        //daysOfWeek
        this.schedule.daysOfWeek = [];
        let dowLimit = Math.pow(2, 7);
        for (var i = 1, n = 0; i < dowLimit; i *= 2, n++) {
            if ((response.DaysOfWeek & i) == i) {
                this.schedule.daysOfWeek[n] = true;
                this.daysOfWeek[n].selected = true;
            } else {
                this.schedule.daysOfWeek[n] = false;
                this.daysOfWeek[n].selected = false;
            }
        }

        //daysOfMonth
        this.daysOfMonthSelected = [];
        let domLimit = Math.pow(2, 31);
        for (var i = 1, n = 0; i < domLimit; i *= 2, n++) {
            if ((response.DaysOfMonth & i) == i) {
                this.schedule.daysOfMonth[n] = true;
                this.daysOfMonthSelected.push(this.daysOfMonthFull[n]);
                this.daysOfMonth[n].selected = true;
            } else {
                this.schedule.daysOfMonth[n] = false;
                this.daysOfMonth[n].selected = false;
            }
        }

        //weeksOfMonth
        this.weeksOfMonthSelected = [];
        let womLimit = Math.pow(2, 5);
        for (var i = 1, n = 0; i < womLimit; i *= 2, n++) {
            if ((response.WeeksOfMonth & i) == i) {
                this.schedule.weeksOfMonth[n] = true;
                this.weeksOfMonthSelected.push(this.weeksOfMonthFull[n]);
                this.weeksOfMonth[n].selected = true;
            } else {
                this.schedule.weeksOfMonth[n] = false;
                this.weeksOfMonth[n].selected = false;
            }
        }

        this.schedule = this.schedulerService.objectifyTheScheduleDates(this.schedule);
        this.isLoading = false;
    }
}
